import { Fragment, useMemo } from "react"
import { MentionThumb } from "@/components/prompt-editor/mention-thumb"
import { mentionDisplayLabel, type MentionItem } from "@/components/prompt-editor/types"
import { splitAgentChatMentions, type AgentChatMentionPart } from "@/lib/agent-image-mentions"
import { cn } from "@/lib/utils"

type Props = {
  text: string
  items: MentionItem[]
}

function NodeMentionChip({ part }: { part: Extract<AgentChatMentionPart, { type: "mention" }> }) {
  const mediaIcon = !part.imageUrl && (part.mediaType === "audio" || part.mediaType === "video") ? part.mediaType : null
  const hasThumb = Boolean(part.imageUrl) || mediaIcon != null
  const tone = part.category === "img" || part.category === "vid" ? "pe-mention--img" : "pe-mention--node"
  return (
    <span className={cn("pe-mention", tone, hasThumb && "pe-mention--has-thumb")}>
      {hasThumb ? (
        <span className="pe-mention__thumb-wrap">
          <MentionThumb icon={mediaIcon} imageUrl={part.imageUrl ?? ""} />
        </span>
      ) : null}
      <span className="pe-mention__label">{mentionDisplayLabel(part.category, part.label)}</span>
    </span>
  )
}

/** User message with `@node[id]`, `@img_N`, and `@vid_N` rendered as thumbnail chips. */
export function AgentNodeMentionText({ text, items }: Props) {
  const parts = useMemo(() => splitAgentChatMentions(text, items), [text, items])
  return (
    <div className="leading-relaxed break-words whitespace-pre-wrap">
      {parts.map((part, index) =>
        part.type === "text" ? (
          <Fragment key={`t-${index}`}>{part.text}</Fragment>
        ) : (
          <NodeMentionChip key={`n-${part.id}-${index}`} part={part} />
        ),
      )}
    </div>
  )
}
